<template>
  <Component
    class="pane-card"
    :class="classes"
    :is="component"
    @click.prevent="goToView"
  >
    <div class="pane-card__container">
      <div
        v-if="iconComponent"
        class="pane-card__icon-grid"
      >
        <div>
          <component :is="iconComponent"></component>
        </div>

        <div>
          <div
            v-if="hasHeader"
            class="pane-card__header"
          >
            <slot name="header">
              <h2
                v-if="heading"
                v-html="heading"
                class="heading heading--small"
              />

              <Transition name="transition-fade">
                <slot name="header-tray">
                  <span
                    v-if="badge && !isLoading"
                    v-html="badge"
                    class="pane-card__header__badge"
                  />

                  <span
                    v-if="tray && !isLoading"
                    v-html="tray"
                    class="pane-card__header__tray"
                  />
                </slot>
              </Transition>
            </slot>
          </div>

          <div class="pane-card__body">
            <div class="pane-card__body__loader">
              <BaseLoaderDefault/>
            </div>

            <slot></slot>
          </div>
        </div>
      </div>

      <div
        v-else-if="hasHeader"
        class="pane-card__header"
      >

        <slot name="header">
          <h2
            v-if="heading"
            v-html="heading"
            class="heading heading--small"
            v-tooltip="{
              theme: 'tooltip-top-right',
              content: headingTooltip
            }"
          />

          <Transition name="transition-fade">
            <slot name="header-tray">
              <span
                v-if="badge && !isLoading"
                v-html="badge"
                class="pane-card__header__badge"
              />

              <span
                v-if="tray && !isLoading"
                v-html="tray"
                class="pane-card__header__tray"
              />

              <RouterLink
                v-if="linkTray && !isLoading"
                class="pane-card__header__link-tray-container"
                :to="linkTo"
              >
                <span class="pane-card__header__link-tray">
                  {{ linkTray }}
                  <SvgIconArrowRight
                    style="height: 9px; transform: translate(-5px, -1px)"
                  />
                </span>
              </RouterLink>
            </slot>
          </Transition>
        </slot>
      </div>

      <div v-if="!iconComponent" class="pane-card__body">
        <div class="pane-card__body__loader">
          <BaseLoaderDefault/>
        </div>

        <slot></slot>
      </div>
    </div>
  </Component>
</template>

<script>
import BaseLoaderDefault from '@/components/base/BaseLoaderDefault'
import BaseGrid from '@/components/base/BaseGrid'

export default {
  name: 'BasePaneCard',

  components: {
    BaseLoaderDefault,
    BaseGrid
  },

  props: {
    isLoading: Boolean,
    heading: String,
    badge: String,
    tray: String,
    linkTray: String,
    linkTo: String,
    highlighted: Boolean,
    unboxed: Boolean,
    opaque: Boolean,
    disabled: Boolean,
    link: String,
    iconComponent: {
      type: String,
      default: ''
    },
    headingTooltip: {
      type: String,
      default: ''
    }
  },

  computed: {
    component () {
      return this.link ? 'a' : 'section'
    },

    hasHeader () {
      return this.heading || this.badge || this.tray || this.linkTray
    },

    classes () {
      return {
        'pane-card--is-loading': this.isLoading,
        'pane-card--highlighted': this.highlighted,
        'pane-card--unboxed': this.unboxed,
        'pane-card--opaque': this.opaque,
        'pane-card--disabled': this.disabled,
        'pane-card--link': this.link
      }
    }
  },

  methods: {
    goToView () {
      if (this.link) {
        this.$router.push(this.link)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$blue-dark: nth($colors-custom, 1);
$blue-light: nth($colors-custom, 2);

.pane-card {
  padding: map-get($layout-insights, 'gap');
  border: 2px solid nth($colors-custom, 8);
  border-radius: map-get($layout-insights, 'border-radius');
  background: #fff;

  &--link:hover {
    border: 2px solid nth($colors-custom, 1);
  }

  &--is-loading & {
    &__body {
      > * {
        visibility: hidden;
      }

      &__loader {
        display: flex;
        visibility: visible;
      }
    }
  }

  &--highlighted {
    border: 2px dashed rgba(nth($colors-custom, 2), 0.8);
  }

  &--unboxed {
    padding: 0;
    border: 0;
    border-radius: 0;
  }

  &--opaque {
    background: nth($colors-custom, 9);

    /deep/ {
      .heading,
      .label {
        color: nth($colors-custom, 1);
      }
    }
  }

  &--disabled {
    @extend %disabled;
  }

  &--opaque & {
    &__body {
      &__loader {
        background: nth($colors-custom, 9);
      }
    }
  }

  &__container {
    display: flex;
    height: 100%;
    flex-direction: column;
  }

  &__icon-grid {
    display: grid;
    grid-template-columns: 1fr 3fr;
    gap: 10px !important;
  }

  &__header {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
    align-items: center;

    &__badge {
      color: nth($colors-custom, 1);
      font: {
        weight: 600;
        size: 13px;
      }
      line-height: 120%;
    }

    &__tray {
      flex-grow: 1;
      font: {
        weight: 500;
        size: 12px;
      }
      line-height: 120%;
      text-align: right;
      color: $blue-dark;
    }

    &__link-tray-container {
      flex-grow: 1;
      font: {
        weight: 500;
        size: 12px;
      }
      line-height: 120%;
      text-align: right;
      color: $blue-dark;
    }

    &__link-tray {
      border-radius: 15px;
      transition: background-color 0.3s;
      padding: 0 0 0 10px;

      &:hover {
        background-color: #F5FBDC;
      }
    }
  }

  &__body {
    flex-grow: 1;
    position: relative;

    &__loader {
      z-index: 90;
      position: absolute;
      left: 0;
      top: 0;
      display: none;
      width: 100%;
      height: 100%;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>
